import type { GoogleFlightOption } from '@app/services/google-flights/google-flights.types';

export enum PortalFeatures {
  portal_hidden_flights_v2 = 'portal_hidden_flights_v2',
  portal_stripe = 'portal_stripe',
  portal_sequentially_ancillaries = 'portal_sequentially_ancillaries',
  portal_tiers = 'portal_tiers',
}

export type PortalFlightRequestNode = {
  isLost: boolean;
  portalLink: string;
  publishedOptions: {
    edges: {
      node: PortalOrderQuoteCardNode;
    }[];
  };
  client?: {
    phone?: string;
  };
};

// Slim subset of ``PortalOrderNode`` returned inside ``portalLead`` for the
// Quote-page card. Fields dropped here are re-hydrated via the standalone
// ``portalOrder(portalLink)`` fetch on option-select (TIC-6859). Keep this in
// sync with ``portalOrderQuoteCardNode`` in ``api.graphql.ts``.
export type PortalOrderQuoteCardNode = Pick<
  PortalOrderNode,
  | 'portalLink'
  | 'alphaName'
  | 'portalDescription'
  | 'cashback'
  | 'savePrice'
  | 'onlinePrice'
  | 'soldAmount'
  | 'soldAmountPerAdult'
  | 'soldAmountPerChild'
  | 'soldAmountPerInfant'
  | 'numberOfAdults'
  | 'numberOfChildren'
  | 'numberOfInfants'
  | 'flightsTotal'
  | 'totalPassengers'
  | 'bookingStatus'
  | 'isChange'
  | 'expertChoice'
  | 'fastest'
  | 'hotelBookings'
  | 'flightItineraries'
>;

export type PortalOrderNode = {
  portalLink: string;
  cashback: {
    eligible: boolean;
    amount: number;
  };
  ancillaries: PortalAncillaryNode[];
  acceptStripe: boolean;
  portalDescription: string;
  soldAmount: number;
  roundTripDepartureAt: string;
  tripType: PortalTripType;
  flightItineraries: PortalFlightItineraryGroupNode[];
  alphaName: string;
  onlinePrice: number;
  savePrice: number;
  bookingStatus: PortalBookingStatusTypeEnum;
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfInfants: number;
  totalPassengers: number;
  soldAmountPerAdult: number;
  soldAmountPerChild: number;
  soldAmountPerInfant: number;
  flightsTotal: number;
  hasThrowawayTicketing: boolean;
  hasHiddenCityTicketing: boolean;
  hotelBookings: PortalHotelBooking[];
  disclaimersHtml: string;
  disclaimersCheckmarkHtml: string;
  disclaimersWithAdditionalProductsHtml: string;
  isDemo: boolean;
  isChange: boolean;
  changeFeePerPax?: number;
  voluntaryChangeFeeMax?: number;
  hiddenFlightsPresented: boolean;
  maxBalanceAmount: number;
  serviceClass: PortalServiceClassType;
  fastest: boolean;
  expertChoice: boolean;
  bookings?: PortalBookingNode[];
  gratuityAmount?: number;
  promocodeAmount?: number;
  ancillaryBundle?: PortalAncillaryBundle;
  salesAgent?: PortalSalesAgentNode;
  tripPortalLink?: string;
  ancillaryUpsellAvailable?: boolean;
};

export type PortalBookingNode = {
  portalLink: string;
  tripType: PortalTripType;
  flightItineraries: PortalFlightItineraryGroupNode[];
  bookingHotel?: BookingHotel;
  eTickets?: PortalETicketNode[];
  airlineConfirmations?: PortalAirlineConfirmationNumberNode[];
};

export type PortalAirlineNode = {
  logoUrl: string;
  shortName: string;
  webSite: string;
};

export type PortalAirportNode = {
  locationCode: string;
  cityName: string;
  locationName: string;
  country: PortalCountryNode;
};

export type PortalCountryNode = {
  name: string;
};

export type LayoverNode = {
  waitTime: TotalTravelTimeNode;
  selfTransfer: boolean;
};

export type PortalFlightItinerarySegmentNode = {
  flightSegment: PortalFlightSegmentNode;
  layover?: LayoverNode;
  travelTime: TotalTravelTimeNode;
  nextDayArrival: boolean;
};

export type PortalFlightSegmentNode = {
  departureAtAirportTimeZone: string;
  arrivalAtAirportTimeZone: string;
  serviceClass: PortalServiceClassType;
  airlineServiceClass: string;
  airline: PortalAirlineNode;
  flightNumber: string;
  aircraftName: string;
  operatedBy: string;
  departureAirport: PortalAirportNode;
  arrivalAirport: PortalAirportNode;
  isOvernight: boolean;
};

export type PortalFlightItineraryGroupNode = {
  travelTime: PortalTotalTravelTimeNode;
  departureAirport: PortalAirportNode;
  arrivalAirport: PortalAirportNode;
  finalDestinationAirport: PortalAirportNode;
  departureAt: string;
  daysShift: number;
  segments: PortalFlightItinerarySegmentNode[];
};

export type TotalTravelTimeNode = {
  days: number;
  hours: number;
  minutes: number;
};

export type PortalTotalTravelTimeNode = TotalTravelTimeNode; // todo merge the types

export type PortalLeadNode = {
  portalLink: string;
  firstName: string;
  lastName: string;
  isNewUser: boolean;
  salesAgent: PortalSalesAgentNode;
  firstTrip?: PortalLeadFirstTripNode;
  flightRequest: PortalFlightRequestNode;
  roundTripDepartureAt: string;
  tripType: PortalTripType;
  optionCount: number;
  minimalOptionPrice: number;
  maximalSecureSavings: number;
  hasNotes: boolean;
  isQuickquote: boolean;
};

export type PortalSalesAgentNode = {
  fullName: string;
  picture: string;
  email: string;
  phone: string;
  deals: number;
  avgSavings: number;
  portalFeatures: PortalFeatures[];
  shift: ShiftNode;
  currency: CurrencyNode;
  agencyName: string;
};

export type CurrencyNode = {
  name: string;
};

export type ShiftNode = {
  startDateTimeAtUtc: string;
  endDateTimeAtUtc: string;
};

export enum PortalTripType {
  Oneway = 'ONEWAY',
  Round = 'ROUND',
  Multi = 'MULTI',
}

export type PortalLeadFirstTripNode = {
  departureAt: string;
  serviceClass: string;
  passengerCount: number;
  departureAirport: PortalAirportNode;
  arrivalAirport: PortalAirportNode;
};

export enum PortalBookingStatusTypeEnum {
  Option = 'OPTION',
  Booked = 'BOOKED',
  InProgress = 'IN_PROGRESS',
  Issued = 'ISSUED',
}

export enum PortalOrderStatusType {
  OptionBooked = 'OPTION_BOOKED',
}

export enum PortalGenderType {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export type AuthorizedDataNode = {
  passengers: PortalPassengerNode[];
  creditCards: PortalCreditCardNode[];
  stripeCreditCards: PortalStripeCreditCardNode[];
  verification: PortalUserVerificationNode;
  quotes: PortalLeadNode[];
  clientBalanceAmount: number;
  upcomingFlightOrders?: { edges: { node: PortalOrderNode }[] };
  pastFlightOrders?: { edges: { node: PortalOrderNode }[] };
  creditBreakdown?: CreditBreakdownNode[];
};

export type PortalPassengerNode = {
  id: string;
  countryId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  birthday: string;
  gender: PortalGenderType;
  passport?: {
    expiration: string;
    number: string;
    country: PortalCountryNode;
  };
  middleName?: string;
  frequentFlyerNumbers?: { number: string; airlineId: string }[];
  emails?: string[];
  phones?: string[];
  globalEntry?: string;
  knownTravelerNumber?: string;
  specialAssistanceId?: string;
  petsId?: string;
  mealPreferencesId?: string;
  seatPreferencesId?: string;
};

export type PortalCreditCardNode = {
  id: string;
  number: string;
  holder: string;
  expiration: string;
  type: CreditCardTypeEnum;
};

export type PortalStripeCreditCardNode = PortalCreditCardNode; // todo merge the types

export enum CreditCardTypeEnum {
  VISA = 'VISA',
  MC = 'MC',
  AMEX = 'AMEX',
  DISC = 'DISC',
  D = 'D',
  JCB = 'JCB',
  EFTPOSAU = 'EFTPOSAU',
  LINK = 'LINK',
  UNIONPAY = 'UNIONPAY',
  UNKNOWN = 'UNKNOWN',
}

export type PortalUserVerificationNode = {
  required?: boolean;
  emailMasked: string;
  emailSentAt: string;
};

export type PortalPassengerNodeInput = {
  id: string;
  gender: PortalGenderType;
  firstName: string;
  lastName: string;
  birthday: Date;
  passportCountryId: string;
  middleName?: string;
};

export type PortalCreditCardNodeInput = {
  id?: string;
  holder?: string;
  number?: string;
  expiration?: string;
  cvv2: string;
  city?: string;
  state?: string;
  zipCode?: string;
  billingAddress1?: string;
  billingAddress2?: string;
  countryId?: string;
  clientBalanceAmount?: number;
  cardAmount?: number;
  phone?: string;
};

export enum PortalServiceClassType {
  FIRST = 'FIRST',
  BUSINESS = 'BUSINESS',
  PREMIUM_ECONOMY = 'PREMIUM_ECONOMY',
  ECONOMY = 'ECONOMY',
  UNDEFINED = 'UNDEFINED',
  MIXED = 'MIXED',
}

export enum AncillaryStatusTypeEnum {
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
  PURCHASED = 'PURCHASED',
}

export type PortalAncillaryBundle = {
  priceWithoutDiscount: number;
  discount: number;
  price: number;
  pricePerTraveler: number;
  priceWithoutDiscountPerTraveler: number;
  ancillaries: PortalBundledAncillary[];
};

export type PortalBundledAncillary = {
  type: AncillaryTypeEnum;
  price: number;
  pricePerTraveler: number;
  priceWithoutDiscountPerTraveler: number;
  priceWithoutDiscount: number;
};

export type BookingHotel = {
  name: string;
  guestCount: number;
  checkInDate: string;
  checkoutDate: string;
  city: string;
};

export type PortalETicketNode = {
  codeNumber: string;
  passenger: PortalPassengerNode;
};

export type PortalAirlineConfirmationNumberNode = {
  code: string;
  airline?: PortalAirlineNode;
};

export type NewLead = {
  departureAirportId: string;
  arrivalAirportId: string;
  passengerCount: number;
  departureAt: string;
  serviceClass?: PortalServiceClassType;
  returnAt?: string;
};

export enum AncillaryTypeEnum {
  DISRUPTION_PROTECTION = 'DISRUPTION_PROTECTION',
  CANCEL_FOR_ANY_REASON = 'CANCEL_FOR_ANY_REASON',
}

export type PortalAncillaryNode = {
  type: AncillaryTypeEnum;
  price: number;
  amountPerTraveler: number;
  refundPerTraveler: number;
  balanceRefundPerTraveler: number;
  status: AncillaryStatusTypeEnum;
};

export type PortalHotelBooking = {
  name: string;
  guestCount: number;
  city: string;
  checkInDate: string;
  checkoutDate: string;
};

export type AvailableUpsellOptionsNode = {
  essential: boolean;
  vip: boolean;
  executive: boolean;
  gratuity: boolean;
};

export enum UpsellEmailVariantEnum {
  EMAIL1 = 'EMAIL1',
  EMAIL2 = 'EMAIL2',
  EMAIL3 = 'EMAIL3',
}

export type AncillaryUpsellNode = {
  ancillaries: PortalAncillaryNode[];
  ancillaryBundle: PortalAncillaryBundle;
  salesAgent: PortalSalesAgentNode;
  acceptStripe: boolean;
  disclaimersHtml: string;
  disclaimersCheckmarkHtml: string;
  disclaimersWithAdditionalProductsHtml: string;
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfInfants: number;
  flightItineraries: PortalFlightItineraryGroupNode[];
  showGratuity: boolean;
  soldAmountPerAdult: number;
  clientPhone?: string;
  emailVariant?: UpsellEmailVariantEnum | null;
  timerExpiresAt?: string | null;
  availableUpsellOptions?: AvailableUpsellOptionsNode | null;
};

export enum GoogleFlightsFeedbackOptionEnum {
  REASONABLE = 'REASONABLE',
  TOO_EXPENSIVE = 'TOO_EXPENSIVE',
  WANT_CONVENIENCE = 'WANT_CONVENIENCE',
  ABOVE_BUDGET = 'ABOVE_BUDGET',
}

export type CreditBreakdownNode = {
  remainingAmount: number;
  expiresAt: string;
};

export enum PortalTierEnum {
  ESSENTIAL = 'ESSENTIAL',
  VIP = 'VIP',
  EXECUTIVE = 'EXECUTIVE',
}

export type PortalTierCoverageBandNode = {
  priceThreshold: number | null;
  coverageAmount: number;
};

export type PortalTierCatalogNode = {
  tierType: PortalTierEnum;
  cashbackPerPassenger: number;
  coverageBands: PortalTierCoverageBandNode[];
};

export enum PortalAutoQuoteStatus {
  OK = 'OK',
  EXPIRED = 'EXPIRED',
  NOT_FOUND = 'NOT_FOUND',
}

export enum AutoQuoteOptionFeedbackEnum {
  LIKED = 'LIKED',
  DISLIKED = 'DISLIKED',
}

export enum PortalQuotePasscodeRole {
  CUSTOMER = 'CUSTOMER',
  AGENT = 'AGENT',
}

export type PortalAutoQuoteOptionNode = Partial<GoogleFlightOption> & {
  optionName: string;
  clientFeedback?: AutoQuoteOptionFeedbackEnum;
};

export type PortalAutoQuoteNode = {
  token: string;
  status: PortalAutoQuoteStatus;
  additionalFeedback?: string;
  options: PortalAutoQuoteOptionNode[];
  expired: boolean;
  departureAirportCode?: string;
  arrivalAirportCode?: string;
  departureCityName?: string;
  arrivalCityName?: string;
  isRoundTrip?: boolean;
  passengerCount?: number;
  salesAgent?: PortalSalesAgentNode;
};
